import client from "./client";
import type { PaginatedResponse } from "@/types/common";

export type DirectoryEmploymentType =
  | "FULL_TIME"
  | "PART_TIME"
  | "CONTRACTOR"
  | "INTERN";

export interface DirectoryDepartment {
  id: string;
  name: string;
}

export interface DirectoryManager {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
}

export interface DirectoryCard {
  id: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  pronouns: string | null;
  title: string;
  work_email: string | null;
  phone: string | null;
  avatar_url: string | null;
  employment_type: DirectoryEmploymentType;
  work_location: string | null;
  office_location: string | null;
  timezone: string | null;
  hire_date: string;
  department: DirectoryDepartment | null;
  manager: DirectoryManager | null;
}

export interface DirectoryProfile extends DirectoryCard {
  direct_reports: DirectoryManager[];
}

export interface DirectoryFilters {
  search?: string;
  department_id?: string;
  location?: string;
  employment_type?: DirectoryEmploymentType;
}

export async function listDirectory(
  page = 1,
  perPage = 24,
  filters?: DirectoryFilters
): Promise<PaginatedResponse<DirectoryCard>> {
  const params: Record<string, string> = {
    page: String(page),
    per_page: String(perPage),
  };
  if (filters?.search) params.search = filters.search;
  if (filters?.department_id) params.department_id = filters.department_id;
  if (filters?.location) params.location = filters.location;
  if (filters?.employment_type) params.employment_type = filters.employment_type;

  const res = await client.get<PaginatedResponse<DirectoryCard>>("/directory", {
    params,
  });
  return res.data;
}

export async function getDirectoryProfile(
  id: string
): Promise<DirectoryProfile> {
  const res = await client.get<DirectoryProfile>(`/directory/${id}`);
  return res.data;
}

export async function listDirectoryLocations(): Promise<string[]> {
  const res = await client.get<string[]>("/directory/locations/list");
  return res.data;
}

// ----- Who's out (reuses /leave/team-availability) -----

export interface WhosOutEntry {
  employee_id: string;
  name: string;
  title: string;
  avatar_url: string | null;
  leave_type: string;
}

export interface TeamAvailability {
  on_date: string;
  out: WhosOutEntry[];
  available: Array<{
    employee_id: string;
    name: string;
    title: string;
    avatar_url: string | null;
  }>;
}

export async function getTeamAvailability(
  onDate?: string
): Promise<TeamAvailability> {
  const params: Record<string, string> = {};
  if (onDate) params.on_date = onDate;
  const res = await client.get<TeamAvailability>("/leave/team-availability", {
    params,
  });
  return res.data;
}
